import React, { useState, useMemo, useRef, useEffect } from "react";
import { ChevronDownIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { openStockInNewTab } from "../utils/openStock";

export default function SidebarStockList({ groupedSyminfo = {}, onSelect, selectedSymbol = '', selectedCategory = '' }) {
  const [openCategory, setOpenCategory] = useState(null);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const activeItemRef = useRef(null);
  const menuRef = useRef(null);
  const [ctxMenu, setCtxMenu] = useState({ open: false, x: 0, y: 0, item: null });
  const containerRef = useRef(null);

  // Whether the active item is actually present in the rendered list of its category.
  const activeRendered = useMemo(() => {
    if (!selectedSymbol || !selectedCategory) return false;
    const rows = groupedSyminfo?.[selectedCategory] || [];
    return rows.some((r) => r.symbol === selectedSymbol);
  }, [groupedSyminfo, selectedCategory, selectedSymbol]);

  // Phase 1: ensure the right category is open and filter cleared
  useEffect(() => {
    if (selectedSymbol && selectedCategory) {
      if (query) setQuery("");
      if (openCategory !== selectedCategory) setOpenCategory(selectedCategory);
    }
  }, [selectedSymbol, selectedCategory]);

  // Phase 2: once the category is open AND the item is actually rendered, scroll to it.
  useEffect(() => {
    if (!selectedSymbol || !selectedCategory) return;
    if (openCategory !== selectedCategory) return;
    if (!activeRendered) return;

    // Try RAF first, then a timeout fallback to cover late layout/virtualization.
    const raf = requestAnimationFrame(() => {
      try {
        if (activeItemRef.current) {
          // Use 'auto' for initial jump; it feels snappier on load.
          activeItemRef.current.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
        }
      } catch {}
    });
    const t = setTimeout(() => {
      try {
        if (activeItemRef.current) {
          activeItemRef.current.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
        }
      } catch {}
    }, 150);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [activeRendered, openCategory, selectedCategory, selectedSymbol]);

  // Close context menu on outside click, ESC, or scroll
  useEffect(() => {
    if (!ctxMenu.open) return;
    const onDown = (e) => {
      if (!menuRef.current) return setCtxMenu({ open: false, x: 0, y: 0, item: null });
      if (!menuRef.current.contains(e.target)) {
        setCtxMenu({ open: false, x: 0, y: 0, item: null });
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setCtxMenu({ open: false, x: 0, y: 0, item: null }); };
    const onScroll = () => setCtxMenu({ open: false, x: 0, y: 0, item: null });
    document.addEventListener('mousedown', onDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [ctxMenu.open]);

  const orderedCategories = useMemo(() => {
    const order = ["NASDAQ", "NYSE", "CRYPTO", "COMMODITIES", "ETF", "FOREX", "BIST"];
    const keys = Object.keys(groupedSyminfo || {});
    return keys.sort((a, b) => (order.indexOf(a) === -1 ? 999 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 999 : order.indexOf(b)) || a.localeCompare(b));
  }, [groupedSyminfo]);

  // First matching symbol for the current filter query (first match in category order).
  const firstMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    for (const cat of orderedCategories) {
      const rows = groupedSyminfo?.[cat] || [];
      const match = rows.find((r) =>
        (r.symbol || "").toLowerCase().includes(q) || (r.name || "").toLowerCase().includes(q)
      );
      if (match) return { ...match, category: cat };
    }
    return null;
  }, [query, orderedCategories, groupedSyminfo]);

  return (
    <div ref={containerRef} className="mt-4 space-y-2 relative">
      {/* Filter box */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            if (e.shiftKey) {
              // Shift+Enter: open first entry in a new tab
              if (firstMatch) {
                e.preventDefault();
                openStockInNewTab(firstMatch.symbol, firstMatch.category);
              }
            } else {
              // Enter: navigate to first entry (in-page)
              if (firstMatch) {
                e.preventDefault();
                onSelect && onSelect(firstMatch);
              }
            }
          }}
          placeholder="Filter by symbol or name"
          aria-label="Filter symbols"
          className="w-full px-3 pr-8 py-2 text-sm placeholder:text-xs rounded-md border dark:bg-gray-900 dark:border-gray-700 dark:text-gray-200"
        />
        {query && (
          <div className="absolute inset-y-0 right-2 flex items-center">
            <button
              type="button"
              onClick={() => {
                setQuery("");
                // Refocus input for quick typing after clear
                if (inputRef.current) inputRef.current.focus();
              }}
              className="leading-none text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
              title="Clear"
              aria-label="Clear filter"
            >
              <XMarkIcon className="h-4 w-4 block" />
            </button>
          </div>
        )}
      </div>
      {orderedCategories.map((cat) => {
        const rows = groupedSyminfo[cat] || [];
        const q = query.trim().toLowerCase();
        const displayRows = q
          ? rows.filter((r) =>
              (r.symbol || "").toLowerCase().includes(q) || (r.name || "").toLowerCase().includes(q)
            )
          : rows;
        const hasMatches = displayRows.length > 0;
        const isOpen = q ? hasMatches : openCategory === cat;
        return (
          <div key={cat} className="border rounded-lg overflow-hidden dark:border-gray-700">
            <button
              type="button"
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
              onClick={q ? undefined : () => setOpenCategory(isOpen ? null : cat)}
              disabled={!!q}
            >
              <span>{cat}</span>
              {isOpen ? (
                <ChevronDownIcon className="h-4 w-4" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" />
              )}
            </button>
            {isOpen && (
              <div className="max-h-64 overflow-auto divide-y dark:divide-gray-800">
                {displayRows.map((row) => {
                  const isActive = row.symbol === selectedSymbol && cat === selectedCategory;
                  return (
                    <button
                      key={`${cat}-${row.symbol}`}
                      type="button"
                      title={row.name || row.symbol || ''}
                      className={`w-full text-left px-3 py-2 text-[11px] sm:text-xs ${isActive ? 'bg-blue-600 text-white' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                      ref={isActive ? activeItemRef : null}
                      onClick={(e) => {
                        if (e.ctrlKey || e.metaKey) {
                          e.preventDefault();
                          openStockInNewTab(row.symbol, cat);
                          return;
                        }
                        onSelect && onSelect({ ...row, category: cat });
                      }}
                      onAuxClick={(e) => {
                        if (e.button === 1) {
                          e.preventDefault();
                          openStockInNewTab(row.symbol, cat);
                        }
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        try {
                          const rect = containerRef.current ? containerRef.current.getBoundingClientRect() : { left: 0, top: 0 };
                          const x = e.clientX - rect.left;
                          const y = e.clientY - rect.top;
                          setCtxMenu({ open: true, x, y, item: { ...row, category: cat } });
                        } catch {
                          setCtxMenu({ open: true, x: e.clientX, y: e.clientY, item: { ...row, category: cat } });
                        }
                      }}
                      aria-label={(row.name || row.symbol || '').toString()}
                    >
                      <span
                        title={row.name || row.symbol || ''}
                        aria-label={(row.name || row.symbol || '').toString()}
                      >
                        {row.symbol}
                      </span>
                    </button>
                  );
                })}
                {!displayRows.length && (
                  <div className="px-3 py-2 text-xs text-gray-500">No symbols</div>
                )}
              </div>
            )}
          </div>
        );
      })}
      {ctxMenu.open && ctxMenu.item && (
        <div
          ref={menuRef}
          className="absolute z-50 min-w-[180px] rounded-md border shadow-lg text-sm bg-white dark:bg-gray-800 dark:border-gray-700"
          style={{
            left: Math.min(
              ctxMenu.x,
              (containerRef.current ? containerRef.current.clientWidth : 240) - 200
            ),
            top: Math.min(
              ctxMenu.y,
              (containerRef.current ? containerRef.current.clientHeight : 400) - 100
            ),
          }}
          role="menu"
        >
          <button
            type="button"
            className="block w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={() => {
              try {
                const url = `${window.location.origin}/summary?stock=${encodeURIComponent(ctxMenu.item.symbol)}&market=${encodeURIComponent(ctxMenu.item.category)}`;
                window.open(url, '_blank', 'noopener');
              } catch {}
              setCtxMenu({ open: false, x: 0, y: 0, item: null });
            }}
            role="menuitem"
          >
            Open in New Tab
          </button>
          <button
            type="button"
            className="block w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={() => {
              try {
                const url = `${window.location.origin}/summary?stock=${encodeURIComponent(ctxMenu.item.symbol)}&market=${encodeURIComponent(ctxMenu.item.category)}`;
                window.open(url, '_blank', 'noopener,width=1200,height=800');
              } catch {}
              setCtxMenu({ open: false, x: 0, y: 0, item: null });
            }}
            role="menuitem"
          >
            Open in New Window
          </button>
        </div>
      )}
    </div>
  );
}
